import { isYes, yesOrNotSelectValidation } from 'helpers';
import { isValidNumber } from 'libphonenumber-js';
import {
  TypedForm,
  ObjectSchema,
  string,
  object,
  date,
  email,
} from 'libs/form';
import { TFunc } from 'libs/i18n';
import { getTodayWithResetHours } from 'modules/client/common/helpers';
import { Maybe } from 'types/maybe';
import { YesOrNoSelectType } from 'types/yesOrNo';

export interface CompanyGlobalFinancialMonitoringFormType {
  globalFinancialMonitoring: {
    isSubjectToRegulation: Maybe<YesOrNoSelectType>;
    supervisoryAuthorityNameAndLocation?: string;

    mandatoryAmlRegulation?: {
      hasMandatoryAmlRegulations?: Maybe<YesOrNoSelectType>;
      regulationDetails?: string;
      authorizedBodyName?: string;
    };

    lastAmlAuditResult?: string;

    investigationsOrSanctions?: {
      hadInvestigationsOrSanctions?: Maybe<YesOrNoSelectType>;
      amlDecisionDateAndAuthorityInfo?: string;
    };

    internalAmlDocuments?: {
      hasInternalAmlDocuments?: Maybe<YesOrNoSelectType>;
      adoptionDate?: Date | null;
      lastUpdateDate?: Date | null;
      hasEffectivenessAssessmentProcedures?: Maybe<YesOrNoSelectType>;
      lastEffectivenessCheckInfo?: string;
      appliesToForeignAffiliates?: Maybe<YesOrNoSelectType>;
    };

    departmentsResponsibleForAmlFunctions?: {
      hasDepartmentsResponsibleForAmlFunctions?: Maybe<YesOrNoSelectType>;
      amlFunctionDepartmentsNames?: string;
    };

    responsibleAmlManager?: {
      hasResponsibleAmlManager?: Maybe<YesOrNoSelectType>;
      fullName?: string;
      position?: string;
      email?: string;
      phoneNumber?: string;
    };

    hasRiskManagementProcedures?: Maybe<YesOrNoSelectType>;

    hasSourceOfFundsProcedures?: Maybe<YesOrNoSelectType>;
    hasRelationshipsWithPEPProcedures?: Maybe<YesOrNoSelectType>;
    hasAnonymousAccounts?: Maybe<YesOrNoSelectType>;
    hasAnonymousAccountPreventionProcedures?: Maybe<YesOrNoSelectType>;
    hasTransactionMonitoringProcedures?: Maybe<YesOrNoSelectType>;
    hasAssetFreezingProcedures?: Maybe<YesOrNoSelectType>;

    hasAMLTrainingProcedures?: Maybe<YesOrNoSelectType>;
    amlTrainingDetails?: string;

    hasBranchesInNonFATFCountries?: Maybe<YesOrNoSelectType>;
    nonFATFBranchesNamesAndLocations?: string;

    hasCorrespondentsInNonFATFCountries?: Maybe<YesOrNoSelectType>;
    nonFATFCorrespondentsNames?: string;

    hasCorrespondentsInOffshoreZones?: Maybe<YesOrNoSelectType>;
    offshoreCorrespondentsNames?: string;

    hasShellBankRelationships?: Maybe<YesOrNoSelectType>;
    shellBanksNames?: string;

    hasShellBankPreventionProcedures?: Maybe<YesOrNoSelectType>;
    hasCDDRequestComplianceProcedures?: Maybe<YesOrNoSelectType>;

    creditRating?: string;
    subsidiariesNamesAndLocations?: string;
    keyPartnersNames?: string;
    foreignBranchesNamesAndLocations?: string;
    financialGroupInfo?: string;

    hasSanctionPolicyDocuments?: Maybe<YesOrNoSelectType>;
    hasSanctionsMonitoringProcedures?: Maybe<YesOrNoSelectType>;
    isUnderSanctionsOrControlledBySanctionedEntity?: Maybe<YesOrNoSelectType>;
    isListedInSanctionLists?: Maybe<YesOrNoSelectType>;
    isLinkedToSanctionedEntities?: Maybe<YesOrNoSelectType>;
  };
}

export const maxLengthTextField = 2000;

const yesOrNoCustomValidation = yesOrNotSelectValidation
  .default(null)
  .required();

export const getCompanyGlobalFinancialMonitoringSchema = (
  t: TFunc
): ObjectSchema<CompanyGlobalFinancialMonitoringFormType> =>
  object({
    globalFinancialMonitoring: object({
      isSubjectToRegulation: yesOrNotSelectValidation.default(null).required(),
    }).when((_, schema, opt) => {
      if (
        isYes(
          (
            opt.value as CompanyGlobalFinancialMonitoringFormType['globalFinancialMonitoring']
          )?.isSubjectToRegulation
        )
      ) {
        return schema.concat(
          object({
            supervisoryAuthorityNameAndLocation: string()
              .required()
              .max(maxLengthTextField),
            mandatoryAmlRegulation: object({
              hasMandatoryAmlRegulations: yesOrNoCustomValidation,

              regulationDetails: string().when('hasMandatoryAmlRegulations', {
                is: isYes,
                then: (schema) => schema.required(),
              }),
              authorizedBodyName: string().when('hasMandatoryAmlRegulations', {
                is: isYes,
                then: (schema) => schema.required(),
              }),
            }).required(),

            lastAmlAuditResult: string().max(maxLengthTextField),

            investigationsOrSanctions: object({
              hadInvestigationsOrSanctions: yesOrNoCustomValidation,
              amlDecisionDateAndAuthorityInfo: string().when(
                'hadInvestigationsOrSanctions',
                {
                  is: isYes,
                  then: (schema) => schema.required().max(maxLengthTextField),
                }
              ),
            }).required(),

            internalAmlDocuments: object({
              hasInternalAmlDocuments: yesOrNoCustomValidation,

              adoptionDate: date()
                .nullable()
                .default(null)
                .when('hasInternalAmlDocuments', {
                  is: isYes,
                  then: (schema) =>
                    schema
                      .required()
                      .max(getTodayWithResetHours(), t('validation.maxDate')),
                }),
              lastUpdateDate: date()
                .nullable()
                .default(null)
                .when('hasInternalAmlDocuments', {
                  is: isYes,
                  then: (schema) =>
                    schema
                      .required()
                      .max(getTodayWithResetHours(), t('validation.maxDate')),
                }),

              hasEffectivenessAssessmentProcedures: yesOrNotSelectValidation
                .default(null)
                .nullable()
                .when('hasInternalAmlDocuments', {
                  is: isYes,
                  then: (schema) => schema.required(),
                }),

              lastEffectivenessCheckInfo: string().when(
                'hasEffectivenessAssessmentProcedures',
                {
                  is: isYes,
                  then: (schema) => schema.required().max(maxLengthTextField),
                }
              ),

              appliesToForeignAffiliates: yesOrNotSelectValidation
                .default(null)
                .nullable(),
            }).required(),

            departmentsResponsibleForAmlFunctions: object({
              hasDepartmentsResponsibleForAmlFunctions: yesOrNoCustomValidation,
              amlFunctionDepartmentsNames: string().when(
                'hasDepartmentsResponsibleForAmlFunctions',
                {
                  is: isYes,
                  then: (schema) => schema.required().max(maxLengthTextField),
                }
              ),
            }).required(),
            responsibleAmlManager: object({
              hasResponsibleAmlManager: yesOrNoCustomValidation,
              fullName: string().when('hasResponsibleAmlManager', {
                is: isYes,
                then: (schema) => schema.required().max(maxLengthTextField),
              }),
              position: string().when('hasResponsibleAmlManager', {
                is: isYes,
                then: (schema) => schema.required().max(maxLengthTextField),
              }),
              email: email(t).when('hasResponsibleAmlManager', {
                is: isYes,
                then: (schema) => schema.required(),
              }),
              phoneNumber: string().when('hasResponsibleAmlManager', {
                is: isYes,
                then: (schema) =>
                  schema
                    .test('phoneNumber', t('validation.phone'), (value) => {
                      if (!value) return true;
                      return isValidNumber(value);
                    })
                    .required()
                    .max(20),
              }),
            }).required(),

            hasRiskManagementProcedures: yesOrNoCustomValidation,

            hasSourceOfFundsProcedures: yesOrNoCustomValidation,
            hasRelationshipsWithPEPProcedures: yesOrNoCustomValidation,
            hasAnonymousAccounts: yesOrNoCustomValidation,
            hasAnonymousAccountPreventionProcedures: yesOrNoCustomValidation,
            hasTransactionMonitoringProcedures: yesOrNoCustomValidation,
            hasAssetFreezingProcedures: yesOrNoCustomValidation,

            hasAMLTrainingProcedures: yesOrNoCustomValidation,
            amlTrainingDetails: string().when('hasAMLTrainingProcedures', {
              is: isYes,
              then: (schema) => schema.required().max(maxLengthTextField),
            }),

            hasBranchesInNonFATFCountries: yesOrNoCustomValidation,
            nonFATFBranchesNamesAndLocations: string().when(
              'hasBranchesInNonFATFCountries',
              {
                is: isYes,
                then: (schema) => schema.required().max(maxLengthTextField),
              }
            ),

            hasCorrespondentsInNonFATFCountries: yesOrNoCustomValidation,
            nonFATFCorrespondentsNames: string().when(
              'hasCorrespondentsInNonFATFCountries',
              {
                is: isYes,
                then: (schema) => schema.required().max(maxLengthTextField),
              }
            ),

            hasCorrespondentsInOffshoreZones: yesOrNoCustomValidation,
            offshoreCorrespondentsNames: string().when(
              'hasCorrespondentsInOffshoreZones',
              {
                is: isYes,
                then: (schema) => schema.required().max(maxLengthTextField),
              }
            ),

            hasShellBankRelationships: yesOrNoCustomValidation,
            shellBanksNames: string().when('hasShellBankRelationships', {
              is: isYes,
              then: (schema) => schema.required().max(maxLengthTextField),
            }),

            hasShellBankPreventionProcedures: yesOrNoCustomValidation,
            hasCDDRequestComplianceProcedures: yesOrNoCustomValidation,

            creditRating: string().max(maxLengthTextField),
            subsidiariesNamesAndLocations: string().max(maxLengthTextField),
            keyPartnersNames: string().max(maxLengthTextField),
            foreignBranchesNamesAndLocations: string().max(maxLengthTextField),
            financialGroupInfo: string().max(maxLengthTextField),

            hasSanctionPolicyDocuments: yesOrNoCustomValidation,
            hasSanctionsMonitoringProcedures: yesOrNoCustomValidation,
            isUnderSanctionsOrControlledBySanctionedEntity:
              yesOrNoCustomValidation,
            isListedInSanctionLists: yesOrNoCustomValidation,
            isLinkedToSanctionedEntities: yesOrNoCustomValidation,
          })
        );
      }
      return schema;
    }),
  });

export const { Field, Form, useFormContext, Submit } =
  TypedForm<CompanyGlobalFinancialMonitoringFormType>();
